import React, {createContext, useContext, useMemo} from 'react'
import PropTypes from 'prop-types'

const EMPTY_ADD_TO_CART_SOURCE = {plpName: null, interactionLocation: null}

const AddToCartSourceContext = createContext(EMPTY_ADD_TO_CART_SOURCE)

/**
 * Publishes where an add_to_cart fired inside this subtree comes from, so that product
 * tiles and quick view can report it without receiving it as a prop. A product list
 * publishes its merchandised name; surfaces that are not a product list (e.g. the search
 * modal) publish an explicit interaction_location instead. Subtrees outside any provider
 * read nulls and fall back to the pathname of the page they are on.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Subtree the source applies to.
 * @param {string} [props.plpName] - Merchandised name of the current product list.
 * @param {string} [props.interactionLocation] - Explicit interaction_location for this surface.
 * @returns {React.ReactElement} Provider element.
 */
export const AddToCartSourceProvider = ({children, plpName, interactionLocation}) => {
    const value = useMemo(
        () => ({plpName: plpName || null, interactionLocation: interactionLocation || null}),
        [plpName, interactionLocation]
    )

    return (
        <AddToCartSourceContext.Provider value={value}>{children}</AddToCartSourceContext.Provider>
    )
}

/**
 * Reads the add_to_cart source published by the surrounding surface.
 * @returns {{plpName: string|null, interactionLocation: string|null}} Current source, or nulls
 * when no surface published one.
 */
export const useAddToCartSource = () => useContext(AddToCartSourceContext)

AddToCartSourceProvider.propTypes = {
    children: PropTypes.node,
    plpName: PropTypes.string,
    interactionLocation: PropTypes.string
}

export default AddToCartSourceContext
